// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";
import merge from "lodash/merge";


interface CreditCardBillingAddress {
    address1: string;
    country: string;
    state: string;
    city: string;
    postalCode: string;
}

interface PaymentPageState {
    creditCardBillingAddress?: CreditCardBillingAddress;
    poNumber?: string;
    lastFour?: string;
    cardType?: string;
    isCreditCard?: boolean;
    paymentMethod?: string;
    expirationDate?: string;
}

export interface XCheckoutStateType {
    isChangingShipToAddress: boolean;
    shipWhenComplete: string;
    payment: PaymentPageState;
    acceptAgreement: boolean;
    carrierAccountNo: string;
}

export const initialCheckoutState: XCheckoutStateType = {
    isChangingShipToAddress: false,
    shipWhenComplete: "false",
    payment: {},
    acceptAgreement: false,
    carrierAccountNo: "",
};

const reducer = {
    "xCheckout/IsChangingShipToAddress": (
        draft: Draft<XCheckoutStateType>,
        action: { isChangingShipToAddress: boolean },
    ) => {
        draft.isChangingShipToAddress = action.isChangingShipToAddress;
    },
    "xCheckout/SetShipWhenComplete": (draft: Draft<XCheckoutStateType>, action: { shipWhenComplete: string }) => {
        draft.shipWhenComplete = action.shipWhenComplete;
    },
    "xCheckout/SetCarrierAccountNo": (draft: Draft<XCheckoutStateType>, action: { carrierAccountNo: string }) => {
        draft.carrierAccountNo = action.carrierAccountNo;
    },
    "xCheckout/SetAcceptAgreement": (draft: Draft<XCheckoutStateType>, action: { acceptAgreement: boolean }) => {
        draft.acceptAgreement = action.acceptAgreement;
    },
    "xCheckout/SetPayment": (draft: Draft<XCheckoutStateType>, action: { payment: PaymentPageState }) => {
        draft.payment = action.payment;
    },
    "xCheckout/Reset": (draft: Draft<XCheckoutStateType>) => {
        draft.isChangingShipToAddress = false;
        draft.shipWhenComplete = "false";
        draft.payment = {};
        draft.acceptAgreement = false;
        draft.carrierAccountNo = "";
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
